/**
 * The price band as schema.org offers, for every page whose JSON-LD describes the product (home,
 * desktop, web app, pricing). The numbers are `plans` in `@transport/product-metadata`; this only
 * shapes them. Prices are in INR; once registered for GST they say they exclude it (`valueAddedTaxIncluded: false`). Fleet carries
 * its price per extra truck beside its base, and Large fleet is by quote, so it carries a "from"
 * (`minPrice`) and no `price`.
 */
import {
  businessPlan,
  fleetPlan,
  freePlan,
  GST_PERCENT,
  GST_REGISTERED,
  largeFleetPlan,
  planTrucksLabel,
  starterPlan,
  urls,
} from '@transport/product-metadata';

const CURRENCY = 'INR';

type UnitPrice = {
  '@type': 'UnitPriceSpecification';
  price?: number;
  minPrice?: number;
  priceCurrency: typeof CURRENCY;
  valueAddedTaxIncluded?: false;
  unitText: string;
  billingDuration?: string;
  referenceQuantity?: { '@type': 'QuantitativeValue'; value: number; unitText: string };
};

const unitPrice = (fields: Omit<UnitPrice, '@type' | 'priceCurrency' | 'valueAddedTaxIncluded'>) =>
  ({
    '@type': 'UnitPriceSpecification',
    priceCurrency: CURRENCY,
    ...(GST_REGISTERED ? { valueAddedTaxIncluded: false as const } : {}),
    ...fields,
  }) satisfies UnitPrice;

const perTruck = { '@type': 'QuantitativeValue', value: 1, unitText: 'truck' } as const;

const terms = `${GST_REGISTERED ? `Excludes GST (${GST_PERCENT}%). ` : ''}No setup fee. Drivers and office users included.`;

export function productOffers() {
  return [
    {
      '@type': 'Offer',
      name: freePlan.name,
      description: `${planTrucksLabel('free')}. No end date.`,
      url: urls.pricing,
      price: 0,
      priceCurrency: CURRENCY,
    },
    ...[starterPlan, businessPlan].map(plan => ({
      '@type': 'Offer',
      name: plan.name,
      description: `${planTrucksLabel(plan.id)}. ${terms}`,
      url: urls.pricing,
      price: plan.yearly,
      priceCurrency: CURRENCY,
      priceSpecification: [
        unitPrice({ price: plan.yearly, unitText: 'year', billingDuration: 'P1Y' }),
        unitPrice({ price: plan.monthly, unitText: 'month', billingDuration: 'P1M' }),
      ],
    })),
    {
      '@type': 'Offer',
      name: fleetPlan.name,
      description: `${planTrucksLabel('fleet')}. ${businessPlan.name}'s price plus each truck above ${businessPlan.maxFleetSize}, at a lower rate as the fleet grows. ${terms}`,
      url: urls.pricing,
      price: businessPlan.yearly,
      priceCurrency: CURRENCY,
      priceSpecification: [
        unitPrice({
          price: fleetPlan.slabs[0].perTruckYearly,
          unitText: 'extra truck per year',
          billingDuration: 'P1Y',
          referenceQuantity: perTruck,
        }),
        unitPrice({
          price: fleetPlan.slabs[0].perTruckMonthly,
          unitText: 'extra truck per month',
          billingDuration: 'P1M',
          referenceQuantity: perTruck,
        }),
      ],
    },
    {
      '@type': 'Offer',
      name: largeFleetPlan.name,
      description: `${planTrucksLabel('large')}, by quote. ${terms}`,
      url: urls.pricing,
      priceCurrency: CURRENCY,
      priceSpecification: unitPrice({
        minPrice: largeFleetPlan.fromPerExtraTruckYearly,
        unitText: 'extra truck per year',
        referenceQuantity: perTruck,
      }),
    },
  ];
}
